import moment from 'moment-timezone';

import { LETTERED_TZ_ABBREVIATIONS } from './timezone-abbreviations';

const isLettered = (abbr: string) => /^[A-Za-z]+$/.test(abbr);

// We only ever show a lettered abbreviation: tzdb and the backend emit bare offsets ("+08", "-03") for many zones.
const toLetteredTzAbbr = (abbr: string, date: moment.Moment, timezone?: string) => {
  if (isLettered(abbr)) return abbr;
  if (!timezone) return '';
  const zoned = date.clone().tz(timezone);
  const fromTzdb = zoned.format('z');
  if (isLettered(fromTzdb)) return fromTzdb;
  const [standard, daylight] = LETTERED_TZ_ABBREVIATIONS[timezone] ?? [];
  return (zoned.isDST() && daylight) || standard || '';
};

const getTzAbbr = (date: Date, timezone?: string) =>
  moment(date)
    .tz(timezone || moment.tz.guess())
    .format('z');

type FormatType = 'date' | 'time' | 'datetime';
type TimePrecision = 'min' | 'sec';
type DatePart = 'month' | 'monthYear' | 'day' | 'year' | 'weekday';

interface FormatDateTimeOptions {
  utcOffset?: number;
  timezone?: string;
  localTimezone?: boolean;
  calculateTzAbbreviation?: boolean;
  timezoneAbbreviation?: string;
  type?: FormatType;
  precision?: TimePrecision;
  part?: DatePart;
  includeWeekday?: boolean;
  includeMonth?: boolean;
  includeYear?: boolean;
  parseZone?: boolean;
  useOrdinalDay?: boolean;
}

export const formatDateTime = (
  dateStr: Date | string | undefined,
  {
    utcOffset,
    timezone,
    timezoneAbbreviation,
    localTimezone,
    calculateTzAbbreviation,
    type = 'date',
    precision = 'min',
    part,
    includeWeekday = false,
    includeMonth = true,
    includeYear = true,
    parseZone = false,
    useOrdinalDay = false,
  }: FormatDateTimeOptions = {}
) => {
  if (!dateStr) return '';

  try {
    let date = parseZone
      ? moment.parseZone(dateStr) // keep original offset
      : moment.utc(dateStr); // normalize to UTC
    let effectiveTimezone = timezone;

    if (!date.isValid()) return '';

    // NOTE: If both `timezone` and `localTimezone` are provided,
    // `localTimezone` takes precedence and overrides `timezone`.
    if (localTimezone) {
      effectiveTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    // NOTE: `parseZone` cannot be combined with `timezone`, `localTimezone`, `utcOffset`, or `calculateTzAbbreviation`
    if (!parseZone) {
      if (effectiveTimezone) {
        date = date.tz(effectiveTimezone);
      } else if (utcOffset !== undefined && timezoneAbbreviation) {
        date = date.add(utcOffset, 'seconds');
      }

      if (calculateTzAbbreviation && effectiveTimezone) {
        timezoneAbbreviation = getTzAbbr(date.toDate(), effectiveTimezone);
      }
    }

    if (part === 'month') {
      return date.format('MMM');
    } else if (part === 'monthYear') {
      return date.format('MMMM YYYY');
    } else if (part === 'day') {
      return date.format('D');
    } else if (part === 'weekday') {
      return date.format('ddd');
    } else if (part === 'year') {
      return date.format('YYYY');
    }

    let dateFormat = '';
    if (includeWeekday) dateFormat += 'ddd ';
    if (includeMonth) dateFormat += 'MMM ';
    dateFormat += useOrdinalDay ? 'Do' : 'D';
    if (includeYear) dateFormat += ' YYYY';

    const timeFormat = precision === 'sec' ? 'h:mm:ss A' : 'h:mm A';

    let result = '';
    if (type === 'datetime') {
      result = date.format(`${dateFormat} ${timeFormat}`);
    } else if (type === 'date') {
      result = date.format(dateFormat);
    } else {
      result = date.format(timeFormat);
    }

    const abbreviation =
      timezoneAbbreviation && toLetteredTzAbbr(timezoneAbbreviation, date, effectiveTimezone);
    if (abbreviation) result += ` ${abbreviation}`;
    return result;
  } catch {
    return '';
  }
};
